import { useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { useBodeguitasLogic } from "./useBodeguitasLogic";
import { Bodeguita, propiedadesApi } from "../../services/propiedades.api";
import { getMsg } from "../../utils/antdStatic";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

export default function BodeguitasTab() {
  const {
    bodeguitas,
    propiedades,
    loading,
    modalOpen,
    editing,
    openCreate,
    openEdit,
    closeModal,
    save,
    remove,
    refresh,
  } = useBodeguitasLogic();

  const [form] = Form.useForm();
  // Filas por página: preferencia de vista, se recuerda por usuario.
  const [pageSize, setPageSize] = useFiltroPersistido<number>(
    "propiedades.bodeguitasFilas",
    20,
    { validar: (v) => typeof v === "number" && v > 0 && v <= 200 },
  );

  useEffect(() => {
    if (modalOpen) {
      form.setFieldsValue(
        editing
          ? { bodeguita: editing.bodeguita, id_propiedad: editing.id_propiedad }
          : { bodeguita: "", id_propiedad: undefined },
      );
    }
  }, [modalOpen, editing, form]);

  const columns: ColumnsType<Bodeguita> = [
    {
      title: "Nombre",
      dataIndex: "bodeguita",
    },
    {
      title: "Propiedad asociada",
      dataIndex: "id_propiedad",
      render: (_: number, record: Bodeguita) =>
        formatPropertyName(record.propiedad?.nombrepropiedad) ||
        `ID: ${record.id_propiedad}`,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 140,
      render: (_: unknown, record: Bodeguita) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Eliminar bodeguita"
            description="Esta accion no se puede deshacer"
            onConfirm={() => remove(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleOk = async () => {
    const values = await form.validateFields();
    await save(values);
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nueva bodeguita
        </Button>
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          Recargar
        </Button>
        <Popconfirm
          title="Crear bodeguitas faltantes"
          description="Se creará una bodeguita para cada propiedad que no tenga una"
          onConfirm={async () => {
            const res = await propiedadesApi.rellenarBodeguitas();
            if (res) {
              getMsg()?.success(res.message);
              refresh();
            }
          }}
          okText="Rellenar"
          cancelText="Cancelar"
        >
          <Button icon={<DatabaseOutlined />}>
            Rellenar faltantes
          </Button>
        </Popconfirm>
      </Space>

      <Table
        rowKey="id"
        dataSource={bodeguitas}
        columns={columns}
        loading={loading}
        pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (_, size) => setPageSize(size) }}
        size="small"
      />

      <Modal
        title={editing ? "Editar bodeguita" : "Nueva bodeguita"}
        open={modalOpen}
        onOk={handleOk}
        onCancel={closeModal}
        okText={editing ? "Guardar" : "Crear"}
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="bodeguita"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Nombre de la bodeguita" />
          </Form.Item>

          <Form.Item
            name="id_propiedad"
            label="Propiedad asociada"
            rules={[{ required: true, message: "Selecciona una propiedad" }]}
          >
            <Select
              placeholder="Buscar propiedad..."
              showSearch
              filterOption={(input, option) =>
                fuzzyIncludes(option?.label, input)
              }
              options={propiedades.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombrepropiedad),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
